import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import {
  Alerts,
  Button,
  ButtonAction,
  Container,
  Header,
  Modal,
  PageContent,
  Spinner,
  Table,
} from 'components';
import { checkPermissions, isLoading, globalPropTypes } from 'helpers';
import { useAccessGuard } from 'hooks';
import {
  loadYcGlobalEventsCreator,
  removeYcGlobalEventCreator,
  setYcGlobalEventsPageCreator,
} from 'store/actionsCreators';
import { CrewChip, CrewList, RowActions } from './GlobalEvents.styled';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ipma.globalEvents',
    url: '',
  },
];

/**
 * Which Young Crews attended which global event, in which year.
 *
 * The YC Management Board keeps this record; a Young Crew states its own
 * participation inside its yearly report, and that report shows what is kept
 * here next to it, so the two can be compared instead of one overwriting the
 * other.
 *
 * Adding and changing an entry happen on screens of their own, the way every
 * other table in the app hands over to a form.
 */
const GlobalEvents = ({ list, loading, userPermissions, loadList, removeEntry, setPage }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { items, page, pages, canManage } = list;

  const [removing, setRemoving] = useState(null);

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: ['ipma_yc_global_events_view'] }, userPermissions || []),
    '/ipma',
    'page.ipma.globalEvents.accessDenied',
  );

  useEffect(() => {
    if (allowed) loadList();
  }, [allowed, page]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  const renderRows = () => {
    if (loading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={canManage ? 4 : 3}>
            <Spinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (!items.length) {
      return (
        <Table.Row>
          <Table.Cell colSpan={canManage ? 4 : 3}>{t('global.noResultsFound')}</Table.Cell>
        </Table.Row>
      );
    }

    return items.map((item) => (
      <Table.Row key={item.id}>
        <Table.Cell>
          <Table.Label>{t('page.ipma.globalEvents.year')}:</Table.Label>
          {item.year}
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.ipma.globalEvents.event')}:</Table.Label>
          {item.eventLabel}
        </Table.Cell>
        <Table.Cell>
          <Table.Label>{t('page.ipma.globalEvents.youngCrews')}:</Table.Label>
          {item.youngCrews.length ? (
            <CrewList>
              {item.youngCrews.map((crew) => (
                <CrewChip key={crew.value}>{crew.label}</CrewChip>
              ))}
            </CrewList>
          ) : (
            '-'
          )}
        </Table.Cell>
        {canManage ? (
          <Table.Cell>
            <Table.Label>{t('page.ipma.globalEvents.action')}:</Table.Label>
            <RowActions>
              <ButtonAction
                as={Link}
                to={`/ipma/global-events/${item.id}/edit`}
                title={t('global.editButton')}
              />
              <Margin left={{ xs: 12 }}>
                <ButtonAction title={t('global.removeButton')} onClick={() => setRemoving(item)} />
              </Margin>
            </RowActions>
          </Table.Cell>
        ) : null}
      </Table.Row>
    ));
  };

  return (
    <>
      <Header breadcrumbs={breadcrumbs} title={t('page.ipma.globalEvents.title')} />
      <PageContent>
        <Container>
          <Alerts type="ipma" />

          <Margin bottom={{ xs: 32 }}>
            <Table tablebreakpoint="lg">
              <Table.Head>
                <Table.Row>
                  <Table.Heading>{t('page.ipma.globalEvents.year')}</Table.Heading>
                  <Table.Heading>{t('page.ipma.globalEvents.event')}</Table.Heading>
                  <Table.Heading>{t('page.ipma.globalEvents.youngCrews')}</Table.Heading>
                  {canManage ? (
                    <Table.Heading>{t('page.ipma.globalEvents.action')}</Table.Heading>
                  ) : null}
                </Table.Row>
              </Table.Head>
              <Table.Body>{renderRows()}</Table.Body>
            </Table>
          </Margin>

          {pages > 1 ? (
            <Margin bottom={{ xs: 24 }}>
              <Button
                secondary
                type="button"
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                {t('global.loadMore')}
              </Button>
            </Margin>
          ) : null}

          {canManage ? (
            <Button
              type="button"
              style={{ width: 'auto' }}
              onClick={() => history.push('/ipma/global-events/create')}
            >
              {t('page.ipma.globalEvents.addEntry')}
            </Button>
          ) : null}

          <Modal
            heading={t('page.ipma.globalEvents.removeConfirm.heading')}
            description={
              removing
                ? t('page.ipma.globalEvents.removeConfirm.description', {
                    event: removing.eventLabel,
                    year: removing.year,
                  })
                : ''
            }
            isOpen={!!removing}
            confirm={() => {
              removeEntry({ id: removing.id });
              setRemoving(null);
            }}
            cancel={() => setRemoving(null)}
            onRequestClose={() => setRemoving(null)}
          />
        </Container>
      </PageContent>
    </>
  );
};

GlobalEvents.propTypes = {
  list: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    page: PropTypes.number,
    pages: PropTypes.number,
    canManage: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loadList: PropTypes.func.isRequired,
  removeEntry: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
};

const mapStateToProps = ({ ipma, user, loading }) => ({
  list: ipma.ycGlobalEvents,
  loading: isLoading(loading, 'GLOBAL'),
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadYcGlobalEventsCreator()),
  removeEntry: (payload) => dispatch(removeYcGlobalEventCreator(payload)),
  setPage: (payload) => dispatch(setYcGlobalEventsPageCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalEvents);
